import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import { Link, LinkProps } from 'react-router'
import { Spinner } from './spinner'
import { cn } from 'src/utils/style'

/**
 * Shared button. Prefer props over `className` for styling — see the button
 * rules in `.claude/rules/buttons.md`. Reach for `variant="unstyled"` only as a
 * last-resort escape hatch.
 */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'text' | 'unstyled'
type ButtonSize = 'sm' | 'md' | 'lg'
type ButtonTone = 'neutral' | 'primary' | 'accent' | 'success' | 'warning' | 'error'
type ButtonShape = 'default' | 'square' | 'circle'

const variantMap: Record<ButtonVariant, string> = {
  primary: 'text-white bg-primary active:bg-primary-300',
  secondary:
    'text-gray-700 border border-border bg-surface hover:bg-surface-hover active:bg-gray-100',
  ghost: 'text-gray-600 hover:bg-surface-hover hover:text-gray-900 active:bg-gray-100',
  danger: 'text-white bg-error active:bg-error-400',
  // Text/link-style button: no fill, underline on hover. Color comes from `tone`.
  text: 'bg-transparent underline-offset-2 hover:underline',
  unstyled: '',
}

// `tone` recolors the text/affordance — meaningful for `ghost` and `text`
// (so semantic-colored buttons stop being bespoke className strings).
const toneMap: Record<ButtonTone, string> = {
  neutral: 'text-text-secondary hover:text-text-primary',
  primary: 'text-primary hover:text-primary-400',
  accent: 'text-accent hover:text-accent-700',
  success: 'text-success hover:text-green-700',
  warning: 'text-warning',
  error: 'text-error hover:text-error-700',
}

// Text-button padding. `md` reproduces the legacy default so existing buttons
// are unchanged; `sm`/`lg` absorb the ad-hoc `!px-* !py-*` overrides.
const sizeMap: Record<ButtonSize, string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-4 py-2',
  lg: 'px-6 py-4 text-lg',
}

// Icon-only buttons are square-ish; no text padding.
const iconSizeMap: Record<ButtonSize, string> = {
  sm: 'p-1',
  md: 'p-2',
  lg: 'p-2.5',
}

const shapeMap: Record<ButtonShape, string> = {
  default: 'rounded-lg',
  square: 'rounded-md',
  circle: 'rounded-full',
}

type CommonButtonProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  tone?: ButtonTone
  /** Render as a compact icon-only button (square padding, no label). Requires `aria-label` or `title`. */
  iconOnly?: boolean
  shape?: ButtonShape
  icon?: ReactNode
}

const buildClassName = ({
  variant,
  size,
  tone,
  iconOnly,
  shape,
  override,
}: {
  variant: ButtonVariant
  size: ButtonSize
  tone?: ButtonTone
  iconOnly: boolean
  shape: ButtonShape
  override?: string
}) => {
  // The hover scale/shadow only applies to substantial text buttons (md/lg,
  // non-icon, filled/outlined). This keeps the legacy default look while
  // sparing compact + icon + text buttons the shadow they used to cancel.
  const emphasize = !iconOnly && variant !== 'text' && variant !== 'unstyled' && size !== 'sm'
  return cn([
    'flex items-center justify-center transition-transform',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:transform-none',
    shapeMap[shape],
    iconOnly ? iconSizeMap[size] : variant === 'text' ? '' : sizeMap[size],
    emphasize && 'hover:scale-[0.98] hover:shadow-lg',
    variantMap[variant],
    tone && (variant === 'ghost' || variant === 'text') ? toneMap[tone] : '',
    override,
  ])
}

const Button = forwardRef<
  HTMLButtonElement,
  CommonButtonProps &
    (
      | ({ link?: undefined; isLoading?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>)
      | {
          link: LinkProps
          children: ReactNode
        }
    )
>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      tone,
      iconOnly = false,
      shape = 'default',
      icon,
      ...props
    },
    ref,
  ) => {
    if ('link' in props && props.link) {
      return (
        <Link
          {...props.link}
          data-variant={variant}
          className={buildClassName({
            variant,
            size,
            tone,
            iconOnly,
            shape,
            override: props.link.className,
          })}
        >
          {children}
          {icon && (
            <div className="ml-[8px] flex h-max w-[20px] items-center justify-center">{icon}</div>
          )}
        </Link>
      )
    }

    const { isLoading, ...rest } = props
    if (import.meta.env.DEV && iconOnly && !rest['aria-label'] && !rest.title) {
      console.warn(
        'Button: icon-only buttons must set `aria-label` (or `title`) for accessibility.',
      )
    }
    return (
      <button
        {...rest}
        ref={ref}
        data-variant={variant}
        className={buildClassName({
          variant,
          size,
          tone,
          iconOnly,
          shape,
          override: cn(isLoading && 'relative', props.className),
        })}
        disabled={props.disabled || isLoading}
        aria-busy={isLoading || undefined}
      >
        {isLoading ? (
          <>
            {/* opacity, not visibility: hidden text would drop the button's accessible name */}
            <span className="inline-flex items-center opacity-0">{children}</span>
            <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
              <Spinner size="xs" />
            </span>
          </>
        ) : (
          children
        )}
        {icon && (
          <div className="ml-[8px] flex h-max w-[20px] items-center justify-center">{icon}</div>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'

export { Button }
export type { ButtonVariant, ButtonSize, ButtonTone, ButtonShape }
